/**
 * WanderingProfilePage — V2 orchestrator for an Accutech wandering device.
 *
 * Counterpart to DeviceProfilePage but intentionally minimal: a single
 * Basic Info tab plus lifecycle actions (Unassign, Delete). No reporting,
 * wellness, or troubleshoot — wandering badges don't have those features.
 *
 * URL (canonical): /community/devices/6/:device_id
 * URL (legacy):    /profiles/accutech/:device_id   (kept for back-compat)
 *
 * Props: id — device_id from the URL
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './wandering-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import DeleteDialog from './components/DeleteDialog.jsx';
import UnassignDialog from './components/UnassignDialog.jsx';

import {
  getSession,
  getCampusId,
  getUserId,
  getSessionId,
  getCampusList,
  updateSessionCampus,
  getRoleWeight,
  getActiveVendorIdList,
} from '../../../services/session.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
} from '../../../libs.jsx';
import {
  fetchWanderingProfile,
  fetchResidentsForAssignment,
  editWanderingProfile,
  unassignWanderingDevice,
  deleteWanderingDevice,
} from './api/wanderingProfileApi.js';

// ==========================================================================
// COMPONENT
// ==========================================================================

const WanderingProfilePage = ({ id }) => {
  const navigate = useNavigate();
  const deviceId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  // --------------------------------------------------------------------------
  // SESSION + PERMISSIONS
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId?.toString()]?.name || s?.campus || '';
  }, [campusId]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  // canDelete is kept around for a future button placement — the handler and
  // dialog are still wired below so wiring a trigger is a one-line change.
  // eslint-disable-next-line no-unused-vars
  const canDelete = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
    vendor_ids: getActiveVendorIdList(),
  }), [campusId]);

  // --------------------------------------------------------------------------
  // DATA
  // --------------------------------------------------------------------------
  const [basicData, setBasicData] = useState(null);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derived fields used across the page
  const deviceName = basicData?.device_name || '';
  const description = basicData?.descriptions || '';
  const serialNum = basicData?.manufacture_uuid || '';
  const modelName = basicData?.model_name || '';
  const residentId = basicData?.resident_id || 0;
  const residentName = basicData?.resident_name || '';
  const isActive = basicData?.is_active;

  // --------------------------------------------------------------------------
  // DRAWERS / DIALOGS / SAVING / TOAST
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }
  const toastTimerRef = useRef(null);
  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // POLLING CYCLE (600s) — basic data + residents for the assign dropdown
  // --------------------------------------------------------------------------
  const loadBasicData = useCallback(async () => {
    if (!campusId || !deviceId) return;
    setLoading(true);
    try {
      const [profileResult, residentsResult] = await Promise.all([
        fetchWanderingProfile(campusId, deviceId),
        fetchResidentsForAssignment(campusId),
      ]);
      setBasicData(Array.isArray(profileResult) && profileResult.length > 0 ? profileResult[0] : null);
      setResidents(Array.isArray(residentsResult) ? residentsResult : []);
    } catch (err) {
      console.error('[WanderingProfilePage] loadBasicData error:', err);
    } finally {
      setLoading(false);
    }
  }, [campusId, deviceId]);

  useEffect(() => { loadBasicData(); }, [loadBasicData]);
  useEffect(() => {
    const t = setInterval(loadBasicData, 600_000);
    return () => clearInterval(t);
  }, [loadBasicData]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  /** Save edits from the BasicInfoDrawer. */
  const handleBasicInfoSave = useCallback(async (formData) => {
    if (!basicData) return;
    setSaving(true);
    const vendor_ids = getActiveVendorIdList();
    const newResidentId = parseInt(formData.residentId || 0, 10);
    const payload = [{
      device_id: deviceId,
      device_name: deviceName,
      descriptions: formData.description,
      is_active: newResidentId !== 0,
      area_id: 0,
      resident_id: newResidentId,
      manufacture_uuid: (formData.serialNum || '').toString(),
      modified_by: getUserId(),
      vendor_ids,
    }];
    const oldPayload = [{
      device_id: deviceId,
      descriptions: description,
      is_active: Number(isActive) ? 1 : 0,
      area_id: 0,
      resident_id: residentId,
      manufacture_uuid: serialNum,
    }];
    const result = await editWanderingProfile(payload, oldPayload, getSessionParams());
    setSaving(false);
    if (result && result[0]) {
      showToast('success', 'Device information saved.');
      setBasicDrawerOpen(false);
      loadBasicData();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [basicData, deviceId, deviceName, description, isActive, residentId, serialNum,
      getSessionParams, showToast, loadBasicData]);

  /** Unassign + deactivate the device (matches DevicesPage wandering tab). */
  const handleUnassign = useCallback(async () => {
    if (!basicData) return;
    setSaving(true);
    const result = await unassignWanderingDevice(basicData, getSessionParams());
    setSaving(false);
    if (result && result[0]) {
      showToast('success', `${deviceName || 'Device'} has been unassigned.`);
      setUnassignDialogOpen(false);
      loadBasicData();
    } else {
      showToast('error', `Failed to unassign${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [basicData, deviceName, getSessionParams, showToast, loadBasicData]);

  /** Permanently delete the device, then navigate back to the wandering tab. */
  const handleDelete = useCallback(async () => {
    if (!basicData) return;
    setSaving(true);
    const result = await deleteWanderingDevice(basicData, getSessionParams());
    setSaving(false);
    if (result && result[0]) {
      showToast('success', `${deviceName || 'Device'} deleted.`);
      // Brief delay so the toast is seen before navigating away.
      setTimeout(() => navigate('/community/wander_devices'), 500);
    } else {
      showToast('error', `Failed to delete${result?.[1] ? ': ' + result[1] : ''}.`);
      setDeleteDialogOpen(false);
    }
  }, [basicData, deviceName, getSessionParams, showToast, navigate]);

  /** Campus change — drop back to the wandering devices index for the new campus. */
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setCampusId(val);
    updateSessionCampus(val);
    navigate('/community/wander_devices');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  const isAssigned = !!residentId && !!Number(isActive);

  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
        <div className="dp-page">

          {/* Toast */}
          {toast ? (
            <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
              {toast.type === 'success' ? (
                <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              )}
              <span>{toast.message}</span>
              <button type="button" onClick={() => setToast(null)} className="dp-toast__close" aria-label="Dismiss">{'✕'}</button>
            </div>
          ) : null}

          {/* Page Header */}
          <header className="dp-page-header">
            <div className="dp-page-header__inner">
              <div className="dp-breadcrumb-row">
                <nav className="dp-breadcrumb">
                  <Link to="/community/wander_devices" className="dp-breadcrumb__text">Wandering Devices</Link>
                  <span className="dp-breadcrumb__separator">/</span>
                  <span className="dp-breadcrumb__current">{deviceName || 'Device Profile'}</span>
                </nav>
                <div className="dp-campus-row">
                  <label htmlFor="wp-campus-selector" className="dp-campus-label">Campus:</label>
                  {campusList.length > 1 ? (
                    <select id="wp-campus-selector" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                      {campusList.map((c) => (
                        <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="dp-campus-name">{campusName}</span>
                  )}
                </div>
              </div>

              {/* Lifecycle actions intentionally removed from the page header
                  for consistency with sibling profile pages (DeviceProfilePage,
                  ResidentProfilePage, AreaProfilePage), which put per-row
                  actions inside section headers rather than at the page level.
                  Unassign still lives in BasicInfoTab's section header.
                  Delete handler + dialog are kept wired below for the eventual
                  button placement — just no trigger in the UI right now. */}
            </div>
          </header>

          {/* Profile Banner */}
          <ProfileBanner
            name={deviceName || 'Wandering Device'}
            subtitle={modelName || 'Accutech badge'}
            metaFields={[
              { label: 'Badge #', value: serialNum || '—' },
              { label: 'Assigned To', value: isAssigned ? residentName : 'Unassigned' },
              { label: 'Status', value: Number(isActive) ? 'Active' : 'Inactive' },
            ]}
            loading={loading}
          />

          {/* Tab Bar — single tab, kept for visual parity with sibling profile pages */}
          <div className="pp-tab-bar">
            <button type="button" className="pp-tab is-active">
              Basic Info
            </button>
          </div>

          {/* Tab Content */}
          <div className="pp-tab-content">
            <BasicInfoTab
              deviceName={deviceName}
              description={description}
              serialNum={serialNum}
              modelName={modelName}
              residentId={residentId}
              residentName={residentName}
              isActive={isActive}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
              onUnassign={() => setUnassignDialogOpen(true)}
            />
          </div>

          {/* Edit Drawer */}
          <BasicInfoDrawer
            open={basicDrawerOpen}
            onClose={() => setBasicDrawerOpen(false)}
            onSave={handleBasicInfoSave}
            saving={saving}
            description={description}
            serialNum={serialNum}
            residentId={residentId}
            residents={residents}
          />

          {/* Unassign Dialog */}
          {unassignDialogOpen ? (
            <UnassignDialog
              deviceName={deviceName}
              residentName={residentName}
              onClose={() => setUnassignDialogOpen(false)}
              onConfirm={handleUnassign}
              saving={saving}
            />
          ) : null}

          {/* Delete Dialog */}
          {deleteDialogOpen ? (
            <DeleteDialog
              deviceName={deviceName}
              onClose={() => setDeleteDialogOpen(false)}
              onConfirm={handleDelete}
              saving={saving}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default WanderingProfilePage;
